// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { useCallback } from "react";
import { Body1, Title1, makeStyles, tokens } from "@fluentui/react-components";
import { useRestartSignIn } from "../../../hooks/useRestartSignIn";
import type { WarmUpFailure } from "../../../services/api/coldStartRetry";
import { SIGN_IN_AGAIN_LABEL } from "../signedOut/signedOutCopy";
import { TRY_AGAIN_LABEL, type WarmUpFailureCopy, warmUpFailureBody } from "./warmUpCopy";

const useStyles = makeStyles({
  root: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: tokens.spacingVerticalXL,
    background: tokens.colorNeutralBackground2,
  },
  card: {
    maxWidth: "460px",
    width: "100%",
    background: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadow8,
    padding: tokens.spacingVerticalXXL,
    display: "grid",
    gap: tokens.spacingVerticalL,
    justifyItems: "start",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  accent: {
    height: "4px",
    width: "72px",
    background: tokens.colorBrandForeground1,
    borderRadius: tokens.borderRadiusMedium,
  },
  body: {
    color: tokens.colorNeutralForeground2,
  },
  cta: {
    display: "inline-block",
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    borderRadius: tokens.borderRadiusMedium,
    fontWeight: tokens.fontWeightSemibold,
    textDecoration: "none",
    cursor: "pointer",
    font: "inherit",
    ":hover": {
      backgroundColor: tokens.colorBrandBackgroundHover,
    },
  },
});

export interface WarmUpFailureCardProps {
  copy: WarmUpFailureCopy;
  failure: WarmUpFailure;
  onRetry: () => void;
}

export function WarmUpFailureCard({ copy, failure, onRetry }: WarmUpFailureCardProps) {
  const styles = useStyles();
  const restartSignIn = useRestartSignIn();
  const signedOut = failure === "signedOut";
  const focusAction = useCallback((element: HTMLButtonElement | null) => {
    element?.focus();
  }, []);
  const signInAgain = useCallback(() => {
    restartSignIn().catch((error: unknown) => {
      console.warn("Sign in again failed", error);
    });
  }, [restartSignIn]);

  return (
    <div className={styles.root}>
      <section className={styles.card} aria-label={copy.title}>
        <div className={styles.accent} />
        <Title1>{copy.title}</Title1>
        <Body1 className={styles.body}>
          {warmUpFailureBody(failure, copy, navigator.onLine)}
        </Body1>
        <button
          ref={focusAction}
          className={styles.cta}
          type="button"
          onClick={signedOut ? signInAgain : onRetry}
        >
          {signedOut ? SIGN_IN_AGAIN_LABEL : TRY_AGAIN_LABEL}
        </button>
      </section>
    </div>
  );
}
